import axiosInstance from './axios/index';

export interface QuickNote {
  createdAt: string;
  text: string;
}

export interface SupervisorVisit {
  actionItems?: string[];
  checkedInAt?: string;
  checkedOutAt?: string;
  companyId: string;
  createdAt: string;
  durationMinutes?: number;
  id: string;
  inspectionId?: string;
  notes?: string;
  plannedAt?: string;
  project: {
    id: string;
    inspectionsEnabled?: boolean;
    proposal?: {
      client?: { id: string };
      lead?: { name?: string };
    };
  };
  purpose?: string;
  quickNotes?: QuickNote[];
  status: 'completed' | 'in_progress' | 'planned';
  supervisor: { firstName: string; id: string; lastName: string };
  type: 'adhoc' | 'scheduled';
}

export interface MySite {
  abeInspectEnabled: boolean;
  id: string;
  inspectionsEnabled: boolean;
  pace?: {
    hoursLast30: number;
    lastVisitAt: string | null;
    visitsLast30: number;
  };
  proposal?: {
    client?: { id: string };
    lead?: { name?: string };
  };
  status: string;
  supervisor?: { firstName: string; id: string; lastName: string } | null;
  supervisorRate?: number;
  supervisorTargetFrequency?: string;
  supervisorTargetHours?: number;
}

export const getMySites = async (): Promise<MySite[]> => {
  const response = await axiosInstance.get('/supervisor/my-sites');
  return response.data;
};

export const createVisit = async (data: {
  notes?: string;
  plannedAt?: string;
  projectId: string;
  purpose: string;
  type: 'adhoc' | 'scheduled';
}): Promise<SupervisorVisit> => {
  const response = await axiosInstance.post('/supervisor/visits', data);
  return response.data;
};

export const addQuickNote = async (
  visitId: string,
  text: string
): Promise<SupervisorVisit> => {
  const response = await axiosInstance.post(
    `/supervisor/visits/${visitId}/quick-note`,
    { text }
  );
  return response.data;
};

export const getActiveVisit = async (): Promise<SupervisorVisit | null> => {
  const response = await axiosInstance.get('/supervisor/visits/active');
  return response.data;
};

export const getMyVisits = async (): Promise<SupervisorVisit[]> => {
  const response = await axiosInstance.get('/supervisor/visits');
  return response.data;
};

export const getVisitsByProject = async (
  projectId: string
): Promise<SupervisorVisit[]> => {
  const response = await axiosInstance.get(
    `/supervisor/visits/project/${projectId}`
  );
  return response.data;
};

export const checkInVisit = async (
  visitId: string
): Promise<SupervisorVisit> => {
  const response = await axiosInstance.patch(
    `/supervisor/visits/${visitId}/check-in`
  );
  return response.data;
};

export const checkOutVisit = async (
  visitId: string,
  notes?: string
): Promise<SupervisorVisit> => {
  const response = await axiosInstance.patch(
    `/supervisor/visits/${visitId}/check-out`,
    { notes }
  );
  return response.data;
};

export const assignSupervisor = async (
  projectId: string,
  data: { supervisorId: string; supervisorRate?: number }
): Promise<void> => {
  await axiosInstance.patch(`/supervisor/project/${projectId}/assign`, data);
};

export const removeSupervisor = async (projectId: string): Promise<void> => {
  await axiosInstance.delete(`/supervisor/project/${projectId}/assign`);
};

export interface SupervisorScheduleEntry {
  companyId: string;
  createdAt: string;
  id: string;
  notes?: string;
  plannedAt: string;
  plannedDurationMinutes: number;
  project: {
    id: string;
    proposal?: { lead?: { name?: string } };
    supervisorTargetFrequency?: string;
    supervisorTargetHours?: number;
  };
  purpose?: string;
  visitId?: string;
}

export const getMySchedule = async (): Promise<SupervisorScheduleEntry[]> => {
  const response = await axiosInstance.get('/supervisor/schedule');
  return response.data;
};

export const createScheduleEntry = async (data: {
  notes?: string;
  plannedAt: string;
  plannedDurationMinutes?: number;
  projectId: string;
  purpose?: string;
}): Promise<SupervisorScheduleEntry> => {
  const response = await axiosInstance.post('/supervisor/schedule', data);
  return response.data;
};

export const deleteScheduleEntry = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/supervisor/schedule/${id}`);
};

export const updateVisitNotes = async (
  visitId: string,
  data: { actionItems?: string[]; notes?: string; status?: string }
): Promise<SupervisorVisit> => {
  const response = await axiosInstance.patch(
    `/supervisor/visits/${visitId}/notes`,
    data
  );
  return response.data;
};
